// @ts-check
// Layout dos e-mails da escola para as famílias (convite de visita, mensagens das conversas e o e-mail das automações),
// com a identidade visual que a escola cadastrou em Configurações > Geral: logo completo no topo, a faixa com as três
// cores, botão na cor principal, marcadores e selo na cor de destaque e o rodapé na cor secundária. Quem recebe é a
// família, então nada aqui leva a marca do Matrícula Mais. Mesmas técnicas do layout do sistema: tabelas e estilos em
// linha, porque Gmail e Outlook ignoram folha de estilo e flexbox. Puro: roda no Worker e na prévia de Configurações >
// Geral, que mostra exatamente o e-mail que vai sair.
import { brandEmailTheme, readBrandColors } from './institution-brand.js';

/** @param {unknown} text */
const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);
/** @param {string} text */
const textToSafeHtml = (text) => escapeHtml(text).replace(/\n/g, '<br>');

const FONT_STACK = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const PAGE_BACKGROUND = '#f3f4f6';
const BODY_TEXT = '#3a4356';
const MUTED_TEXT = '#6b7385';
const LINE = '#e5e7eb';

/**
 * @typedef {{ name: string, colors: import('./institution-brand.js').BrandColors, logoUrl: string | null, iconUrl: string | null }} InstitutionBrand
 * @typedef {{ label: string, value: string }} InstitutionEmailDetail
 */

/**
 * Endereço público do arquivo da escola (o caminho /auth/logos/... é servido pelo próprio host da escola).
 * @param {{ ROOT_DOMAIN: string, APP_DEV_PORT?: string }} env @param {string} slug @param {string | null | undefined} path
 */
export function institutionAssetUrl(env, slug, path) {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  const base = env.ROOT_DOMAIN === 'localhost' ? `http://${slug}.localhost:${env.APP_DEV_PORT ?? '8787'}` : `https://${slug}.${env.ROOT_DOMAIN}`;
  return `${base}${path}`;
}

/**
 * Identidade da escola a partir da linha do tenant no D1 core (paleta padrão enquanto ela não escolheu a dela).
 * @param {{ ROOT_DOMAIN: string, APP_DEV_PORT?: string }} env
 * @param {{ slug: string, displayName?: string | null, logoUrl?: string | null, brandLogoUrl?: string | null, brandColors?: unknown }} tenant
 * @returns {InstitutionBrand}
 */
export function institutionBrandOf(env, tenant) {
  return {
    name: tenant.displayName || tenant.slug,
    colors: readBrandColors(tenant.brandColors ?? null),
    logoUrl: institutionAssetUrl(env, tenant.slug, tenant.brandLogoUrl),
    iconUrl: institutionAssetUrl(env, tenant.slug, tenant.logoUrl),
  };
}

/** @param {InstitutionBrand} brand @param {ReturnType<typeof brandEmailTheme>} theme */
function renderHeader(brand, theme) {
  const name = escapeHtml(brand.name);
  const identity = brand.logoUrl
    ? `<img src="${escapeHtml(brand.logoUrl)}" alt="${name}" height="44" style="display:block;height:44px;width:auto;max-width:240px;border:0">`
    : `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
        ${brand.iconUrl ? `<td style="padding:0 12px 0 0;vertical-align:middle"><img src="${escapeHtml(brand.iconUrl)}" alt="" width="40" height="40" style="display:block;width:40px;height:40px;border-radius:10px;border:0"></td>` : ''}
        <td style="vertical-align:middle;font-family:${FONT_STACK};font-size:19px;font-weight:700;color:${theme.ink}">${name}</td>
      </tr></table>`;
  // faixa com as três cores da escola, na proporção 60/25/15
  const stripe = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td width="60%" height="6" bgcolor="${theme.primary}" style="background:${theme.primary};font-size:0;line-height:0">&nbsp;</td>
      <td width="25%" height="6" bgcolor="${theme.secondary}" style="background:${theme.secondary};font-size:0;line-height:0">&nbsp;</td>
      <td width="15%" height="6" bgcolor="${theme.accent}" style="background:${theme.accent};font-size:0;line-height:0">&nbsp;</td>
    </tr></table>`;
  return `<tr><td bgcolor="#ffffff" style="background:#ffffff;padding:24px 32px;border-radius:18px 18px 0 0">${identity}</td></tr>
  <tr><td style="padding:0">${stripe}</td></tr>`;
}

/**
 * Monta o e-mail da escola. Parágrafos, citação e nota chegam em HTML seguro (quem chama escapa o que vem de fora).
 * @param {{
 *   brand: InstitutionBrand, preheader: string, title?: string | null, badge?: string | null, paragraphs?: string[],
 *   details?: InstitutionEmailDetail[], quote?: string | null, button?: { label: string, url: string } | null, note?: string | null,
 *   footerLines?: string[],
 * }} input
 */
export function renderInstitutionEmail(input) {
  const theme = brandEmailTheme(input.brand.colors);
  const badge = input.badge
    ? `<tr><td style="padding:0 0 14px"><span style="display:inline-block;background:${theme.accentSoft};color:${theme.ink};font-family:${FONT_STACK};font-size:12px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;line-height:1;padding:7px 12px 7px 10px;border-radius:999px"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${theme.accent};margin-right:6px;vertical-align:1px"></span>${escapeHtml(input.badge)}</span></td></tr>`
    : '';
  const title = input.title
    ? `<tr><td style="padding:0 0 14px;font-family:${FONT_STACK};font-size:26px;line-height:1.25;font-weight:700;color:${theme.ink}">${escapeHtml(input.title)}</td></tr>`
    : '';
  const paragraphs = (input.paragraphs ?? []).map((paragraph) => `<tr><td style="padding:0 0 16px;font-family:${FONT_STACK};font-size:16px;line-height:1.6;color:${BODY_TEXT}">${paragraph}</td></tr>`).join('');
  const details = input.details?.length
    ? `<tr><td style="padding:2px 0 22px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid ${LINE};border-radius:14px">${input.details.map((detail, index) => `<tr>
        <td width="14" style="padding:${index === 0 ? 16 : 12}px 0 0 18px;vertical-align:top"><div style="width:8px;height:8px;margin-top:6px;border-radius:2px;background:${theme.accent}"></div></td>
        <td style="padding:${index === 0 ? 16 : 12}px 18px 0 10px;vertical-align:top;font-family:${FONT_STACK}">
          <div style="font-size:12px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:${MUTED_TEXT}">${escapeHtml(detail.label)}</div>
          <div style="font-size:16px;line-height:1.45;font-weight:600;color:${theme.ink}">${escapeHtml(detail.value)}</div>
        </td></tr>`).join('')}<tr><td colspan="2" style="padding:0 0 16px"></td></tr></table></td></tr>`
    : '';
  const quote = input.quote
    ? `<tr><td style="padding:0 0 22px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-left:4px solid ${theme.accent};background:${theme.primarySoft};border-radius:0 12px 12px 0;padding:14px 18px;font-family:${FONT_STACK};font-size:15px;line-height:1.55;color:${BODY_TEXT}">${input.quote}</td></tr></table></td></tr>`
    : '';
  const button = input.button
    ? `<tr><td style="padding:6px 0 24px"><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${theme.primary}" style="border-radius:12px;background:${theme.primary}"><a href="${escapeHtml(input.button.url)}" target="_blank" style="display:inline-block;padding:15px 26px;font-family:${FONT_STACK};font-size:16px;font-weight:700;line-height:1;color:${theme.onPrimary};text-decoration:none;border-radius:12px">${escapeHtml(input.button.label)}</a></td></tr></table></td></tr>`
    : '';
  const note = input.note
    ? `<tr><td style="padding:4px 0 0;border-top:1px solid ${LINE}"><p style="margin:16px 0 0;font-family:${FONT_STACK};font-size:13px;line-height:1.55;color:${MUTED_TEXT}">${input.note}</p></td></tr>`
    : '';
  const footerLines = (input.footerLines ?? []).map((line) => `<p style="margin:6px 0 0;font-family:${FONT_STACK};font-size:12px;line-height:1.5;color:${theme.onSecondary};opacity:0.85">${line}</p>`).join('');
  const footerIcon = input.brand.iconUrl
    ? `<td style="padding:0 10px 0 0;vertical-align:middle"><img src="${escapeHtml(input.brand.iconUrl)}" alt="" width="28" height="28" style="display:block;width:28px;height:28px;border-radius:7px;border:0"></td>`
    : '';

  return `<!doctype html>
<html lang="pt-BR" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml(input.title ?? input.brand.name)}</title>
<style>
  @media (max-width: 620px) {
    .school-shell { padding: 16px 10px !important; }
    .school-card { padding: 26px 22px 22px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:${PAGE_BACKGROUND};-webkit-text-size-adjust:100%">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:${PAGE_BACKGROUND}">${escapeHtml(input.preheader)}&#8199;&#847;&#8199;&#847;&#8199;&#847;&#8199;&#847;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${PAGE_BACKGROUND}" style="background:${PAGE_BACKGROUND}">
  <tr><td align="center" class="school-shell" style="padding:32px 16px">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:580px">
      ${renderHeader(input.brand, theme)}
      <tr><td class="school-card" bgcolor="#ffffff" style="background:#ffffff;padding:32px 32px 28px">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
          ${badge}${title}${paragraphs}${details}${quote}${button}${note}
        </table>
      </td></tr>
      <tr><td bgcolor="${theme.secondary}" style="background:${theme.secondary};padding:18px 32px 20px;border-radius:0 0 18px 18px">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
          ${footerIcon}
          <td style="vertical-align:middle;font-family:${FONT_STACK};font-size:14px;font-weight:700;color:${theme.onSecondary}">${escapeHtml(input.brand.name)}</td>
        </tr></table>
        ${footerLines}
      </td></tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;
}

/**
 * Mensagem escrita pela equipe (conversa) ou por uma automação: só o texto, com o logo e as cores da escola em volta.
 * Parágrafos separados por linha em branco, como o texto simples que vai junto.
 * @param {InstitutionBrand} brand @param {string} text @param {{ signature?: string | null }} [options]
 */
export function renderInstitutionMessageHtml(brand, text, options = {}) {
  const paragraphs = String(text ?? '').trim().split(/\n{2,}/).filter(Boolean).map((paragraph) => textToSafeHtml(paragraph));
  return renderInstitutionEmail({
    brand,
    preheader: String(text ?? '').replace(/\s+/g, ' ').trim().slice(0, 140),
    paragraphs,
    footerLines: options.signature ? [escapeHtml(options.signature)] : [],
  });
}
